import {type ChartAggOption} from '../../../generated/graphql';
import {type Line, type LineWithSource} from '../../../util/plotHelpers/types';
import {type PrimaryLineType} from '../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {parseHistoryColumnar} from '../bucketedData/columnar/parseColumnarHistory';
import {BIN_POINT_TO_USE} from './../../../util/plotHelpers/chartTypes';
import {type PointWithMeta} from './buildPoints';
import {parseHistory} from './parseHistory';
import {
  type HistoryColumns,
  type HistoryRecord,
  type LineAggregation,
  type ParseHistoryConfig,
} from './types';

export function addSmoothedLineFromBackend(
  line: Line,
  {
    agg,
    behavior,
    smoothedMetric,
    finitePoints,
    groupAgg,
    isRunsetGrouped,
    matchedHistory,
    matchedColumnar,
    primaryLineType,
    xAxis,
    xMetrics,
  }: {
    agg: LineAggregation;
    behavior: {
      usingExpressions: boolean;
    };
    smoothedMetric: string;
    finitePoints: PointWithMeta[];
    isRunsetGrouped: boolean;
    groupAgg?: ChartAggOption;
    matchedHistory: Record<string, any>[];
    matchedColumnar?: HistoryColumns;
    primaryLineType?: PrimaryLineType;
    xAxis: string;
    xMetrics: string[];
  }
) {
  const finiteByX = new Map<number, PointWithMeta>();
  finitePoints.forEach(p => {
    finiteByX.set(p.x, p);
  });

  const parseConfig: ParseHistoryConfig = {
    agg,
    startTime: {
      value: line.startTimeInMs!,
      unit: 'milliseconds',
    },
    usingExpressions: behavior.usingExpressions,
    primaryLineType,
  };
  const otherMetrics = xMetrics.filter(x => x !== xAxis);
  // we don't allow targeting min/max outside of groups currently
  const parseGroupAgg = isRunsetGrouped
    ? groupAgg
    : (BIN_POINT_TO_USE.toLowerCase() as ChartAggOption);

  const smoothedPoints = matchedColumnar
    ? parseHistoryColumnar(
        [matchedColumnar],
        smoothedMetric,
        xAxis,
        otherMetrics,
        parseConfig,
        parseGroupAgg
      )
    : parseHistory(
        matchedHistory as HistoryRecord[],
        smoothedMetric,
        xAxis,
        otherMetrics,
        parseConfig,
        parseGroupAgg
      );

  // Create a complete smoothed line.
  // Note: this function is only called for frontend-processed lines, so source is always 'frontend'
  const smoothedLine: LineWithSource = {
    ...line,
    source: 'frontend',
    smoothed: true,
    meta: {
      ...line.meta,
      category: 'smoothed',
    },
    data: smoothedPoints.finitePoints.map(p => ({
      x: p.x,
      y: p.y,
      legendData: {
        original: finiteByX.get(p.x)?.y,
      },
    })),
  };

  line.smoothedLineFromBackend = smoothedLine;
}
